/**
 * A family member's avatar: their photo when they have one, their initials in
 * their color when they don't (or if the photo fails to load).
 *
 * The R2 bucket is private, so a photo only ever arrives through a Worker proxy
 * (`/api/location/:id/avatar` for anyone on the map, `/api/profile/avatar` for
 * yourself). Pass that URL as `url`; pass `null` and this is just the colored
 * initials circle it has always been.
 */
import { useEffect, useState } from 'react';
import { initialsOf } from '../lib/initials';

interface Props {
  name: string;
  color?: string | null;
  /** Proxied avatar URL, or null for the initials fallback. */
  url?: string | null;
  /** Tailwind size classes for the circle, e.g. "h-11 w-11". */
  className?: string;
  /** Text size for the initials fallback, e.g. "text-sm". */
  textClassName?: string;
}

export default function Avatar({
  name,
  color,
  url,
  className = 'h-11 w-11',
  textClassName = 'text-sm',
}: Props) {
  // A photo that 404s or is a broken format falls back to initials, never a torn image.
  const [broken, setBroken] = useState(false);
  // A new url (e.g. a fresh upload, cache-busted by ?v=) deserves a fresh attempt —
  // otherwise a once-broken instance would show initials forever.
  useEffect(() => setBroken(false), [url]);
  const showPhoto = Boolean(url) && !broken;

  return (
    <span
      aria-hidden="true"
      className={`relative grid shrink-0 place-items-center overflow-hidden rounded-full font-semibold text-white ${className} ${textClassName}`}
      style={{ backgroundColor: color ?? 'var(--color-ink-faint)' }}
    >
      {showPhoto ? (
        <img
          src={url ?? undefined}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}
